'use client';

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';

import { W, T, FONT } from './tokens';
import { EVENT_TYPES } from './event-types';

/**
 * The one place every call to action on this page lands.
 *
 * It posts to /api/beta/interest, which ALREADY EXISTS and already does the
 * right things: it emails hello@fitvent.com with the request, sends the person
 * a confirmation, and files the request for the admin beta-requests screen. So
 * "Tell us you run this", "Book a walkthrough" and "Talk to us" all come here
 * rather than to /sign-up, which is what they used to do and which asked
 * somebody to open an account before they had been told anything.
 *
 * A form rather than a mailto: a mailto loses everything about who asked, opens
 * whatever mail client the machine happens to have configured, and silently
 * does nothing on a machine with none. This captures the organisation and the
 * event type, which is exactly what you need before a walkthrough.
 *
 * No calendar booking. Live scheduling would promise availability nobody has
 * committed to yet.
 *
 * The two choices are dropdowns rather than free text, following the existing
 * beta form in components/landing/FinalCta.tsx: curated options are quicker to
 * answer and give you data you can actually sort. They are NOT native selects,
 * which CLAUDE.md rules out and which that older form still uses: a native
 * popup is drawn by the operating system and ignores this page entirely.
 *
 * The event types are THE SAME TWELVE the tiles show, plus an escape hatch. An
 * organiser who has just read that their sport is coming should find it in this
 * list; a separate hand-written list would drift from the tiles within a month.
 */

const FIELD: React.CSSProperties = {
  width: '100%',
  fontFamily: FONT,
  // 16px minimum, or Safari on iOS zooms the page when the field takes focus.
  fontSize: 16,
  color: W.bone,
  background: 'rgba(255,255,255,0.04)',
  border: `1px solid ${W.darkBorder}`,
  borderRadius: 8,
  padding: '11px 13px',
  outline: 'none',
};

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 13.5,
  color: W.dim,
  marginBottom: 6,
};

const EVENT_TYPE_OPTIONS = [...EVENT_TYPES.map((t) => t.name), 'Something else'];

const LIST: React.CSSProperties = {
  position: 'absolute',
  zIndex: 5,
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  maxHeight: 232,
  overflowY: 'auto',
  display: 'block',
  padding: 5,
  borderRadius: 10,
  border: `1px solid ${W.darkBorder}`,
  background: '#10201D',
  boxShadow: '0 18px 40px rgba(0,0,0,0.45)',
};

const OPTION: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  width: '100%',
  textAlign: 'left',
  font: 'inherit',
  fontSize: 15,
  color: W.bone,
  border: 'none',
  borderRadius: 7,
  padding: '9px 11px',
  cursor: 'pointer',
};

const PARTICIPANT_RANGES = ['Under 250', '250 to 1,000', '1,000 to 5,000', '5,000 to 20,000', '20,000+'];

/**
 * A dropdown that belongs to this page rather than to the operating system.
 * Closes on outside click and on Escape, which a native select gives you free
 * and a div does not.
 */
function Choice({
  id, name, label, options, placeholder,
}: {
  id: string;
  name: string;
  label: string;
  options: string[];
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [hover, setHover] = useState<string | null>(null);
  const wrap = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span ref={wrap} style={{ position: 'relative', display: 'block' }}>
      <label style={LABEL} htmlFor={id}>{label}</label>
      <input type="hidden" name={name} value={value} readOnly />
      <button
        id={id}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          ...FIELD,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textAlign: 'left',
          cursor: 'pointer',
          color: value ? W.bone : W.muteL,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value || placeholder}
        </span>
        <ChevronDown size={15} color={W.dim} style={{ flex: 'none' }} />
      </button>

      {/* Styled inline rather than through a class, like everything else on
          this page. A class here is one more thing that has to survive the CSS
          pipeline for the control to be usable at all, and this one did not. */}
      {open && (
        <span role="listbox" style={LIST}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={option === value}
              onClick={() => {
                setValue(option);
                setOpen(false);
              }}
              onMouseEnter={() => setHover(option)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(option)}
              onBlur={() => setHover(null)}
              style={{
                ...OPTION,
                background: hover === option ? 'rgba(42,223,223,0.12)' : 'transparent',
              }}
            >
              {option}
              {option === value && <Check size={14} strokeWidth={2.4} color={W.ice} />}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export function ContactForm() {
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    setError(null);

    const data = new FormData(e.currentTarget);
    try {
      const res = await fetch('/api/beta/interest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: String(data.get('firstName') ?? ''),
          email: String(data.get('email') ?? ''),
          organisation: String(data.get('organisation') ?? ''),
          eventType: String(data.get('eventType') ?? ''),
          annualParticipants: String(data.get('annualParticipants') ?? ''),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? 'Something went wrong. Try again, or email hello@fitvent.com.');
      }
      setState('sent');
    } catch (err) {
      setState('idle');
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  if (state === 'sent') {
    return (
      <div
        style={{
          display: 'flex', gap: 14, alignItems: 'flex-start',
          background: 'rgba(42,223,223,0.07)', border: `1px solid ${W.darkBorder}`,
          borderRadius: 14, padding: '26px 28px', maxWidth: 560,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 34, height: 34, flex: 'none', borderRadius: '50%', background: W.ice,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Check size={18} strokeWidth={2.6} color={W.obsidian} />
        </span>
        <span>
          <span style={{ display: 'block', fontSize: 19, fontWeight: 700, color: W.bone, marginBottom: 6 }}>
            Got it, thanks.
          </span>
          <span style={{ ...T.body, display: 'block', color: W.dim }}>
            We will come back to you at that address. If it is urgent, hello@fitvent.com reaches the same inbox.
          </span>
        </span>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 560 }} noValidate>
      <div className="fvw-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
        <span>
          <label style={LABEL} htmlFor="fvw-name">Your name</label>
          <input id="fvw-name" name="firstName" required autoComplete="given-name" style={FIELD} />
        </span>
        <span>
          <label style={LABEL} htmlFor="fvw-email">Email</label>
          <input id="fvw-email" name="email" type="email" required autoComplete="email" style={FIELD} />
        </span>
        <span>
          <label style={LABEL} htmlFor="fvw-org">Organisation</label>
          <input id="fvw-org" name="organisation" autoComplete="organization" style={FIELD} />
        </span>
        <Choice
          id="fvw-type"
          name="eventType"
          label="What do you run?"
          placeholder="Pick one"
          options={EVENT_TYPE_OPTIONS}
        />
        <Choice
          id="fvw-size"
          name="annualParticipants"
          label="Athletes a year"
          placeholder="Roughly is fine"
          options={PARTICIPANT_RANGES}
        />
      </div>

      {error && (
        <p style={{ ...T.small, color: '#FF9E9E', margin: '14px 0 0' }} role="alert">
          {error}
        </p>
      )}

      <button type="submit" className="fvw-cta" style={submitButton} disabled={state === 'sending'}>
        {state === 'sending' && <Loader2 size={16} className="fvw-spin" />}
        {state === 'sending' ? 'Sending' : 'Send this to us'}
      </button>

      <p style={{ ...T.small, color: W.muteL, margin: '12px 0 0' }}>
        Straight to hello@fitvent.com. No newsletter, no sequence.
      </p>
    </form>
  );
}

const submitButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  marginTop: 20,
  background: W.ice,
  color: W.obsidian,
  fontFamily: FONT,
  fontSize: 15,
  fontWeight: 600,
  padding: '12px 22px',
  borderRadius: 6,
  border: 'none',
  cursor: 'pointer',
};
